<template>
  <div class="bank-details-step">
    <!-- Overridable: the post-settlement modal replaces this with its own summary banner. -->
    <slot name="description">
      <p class="info-box">
        The bank details will be used to deposit your initial lump sum and any ongoing scheduled equity release payments
        (if applicable), and to process your monthly interest repayments.
      </p>
    </slot>

    <!-- data-private masks the account fields (and what the customer types into them) in LogRocket
         replays; the attribute covers every descendant, so it sits on the wrapper once. -->
    <div class="inputs" data-private>
      <TextField
        width="100%"
        placeholder="Name on account"
        label="Name on account"
        :maxLength="ACCOUNT_NAME_MAX_LENGTH"
        :value="value.bankAccountName"
        :errorMessages="errors.bankAccountName"
        @onInput="updateField('bankAccountName', sanitizeText($event, ACCOUNT_NAME_MAX_LENGTH))"
      />
      <TextField
        width="100%"
        placeholder="Name of financial institution"
        label="Name of financial institution"
        :value="value.financialInstitutionName"
        :errorMessages="errors.financialInstitutionName"
        @onInput="updateField('financialInstitutionName', $event)"
      />
      <TextField
        width="100%"
        placeholder="BSB"
        label="BSB"
        trailingIcon="pi pi-lock"
        numeric
        :maxLength="BSB_MAX_LENGTH"
        :value="value.bsb"
        :errorMessages="errors.bsb"
        @onInput="updateField('bsb', sanitizeDigits($event, BSB_MAX_LENGTH))"
      />
      <TextField
        width="100%"
        placeholder="Account number"
        label="Account number"
        trailingIcon="pi pi-lock"
        numeric
        :maxLength="ACCOUNT_NUMBER_MAX_LENGTH"
        :value="value.accountNumber"
        :errorMessages="errors.accountNumber"
        @onInput="updateField('accountNumber', sanitizeDigits($event, ACCOUNT_NUMBER_MAX_LENGTH))"
      />
    </div>

    <div class="mt-4 authority">
      <Checkbox
        id="direct-debit-authority"
        :label="directDebitAuthorityLabel"
        :value="value.directDebitAuthority"
        @getInputValue="updateField('directDebitAuthority', $event)"
      >
        I/we have read and agree to the terms of the <ServiceAgreementLink :url="serviceAgreementUrl" /> and authorise
        Stripe Payments Australia Pty Ltd ACN 160 180 343 Direct Debit User ID number 507156 ("Stripe") to debit my/our
        account specified above through the Bulk Electronic Clearing System ("BECS") on behalf of Bridgit Financial
        Services Pty Ltd ("Bridgit") for any amounts that are due by me/us in connection with my/our loan with Bridgit.
      </Checkbox>
      <label v-if="errors.directDebitAuthority" class="error-text">{{ errors.directDebitAuthority }}</label>
    </div>

    <div class="mt-1 authority">
      <Checkbox
        id="account-holder-authority"
        :label="accountHolderAuthorityLabel"
        :value="value.accountHolderAuthority"
        @getInputValue="updateField('accountHolderAuthority', $event)"
      />
      <label v-if="errors.accountHolderAuthority" class="error-text">{{ errors.accountHolderAuthority }}</label>
    </div>
  </div>
</template>

<script>
import {
  ACCOUNT_NAME_MAX_LENGTH,
  ACCOUNT_NUMBER_MAX_LENGTH,
  BSB_LENGTH,
  sanitizeDigits,
  sanitizeText,
} from '@/services/bankDetailsValidation';
import ServiceAgreementLink from '@/components/common/ServiceAgreementLink.vue';

const DIRECT_DEBIT_AUTHORITY_TEXT =
  'I/we have read and agree to the terms of the Direct Debit Request Service Agreement and authorise Stripe ' +
  'Payments Australia Pty Ltd ACN 160 180 343 Direct Debit User ID number 507156 ("Stripe") to debit my/our ' +
  'account specified above through the Bulk Electronic Clearing System ("BECS") on behalf of Bridgit Financial ' +
  'Services Pty Ltd ("Bridgit") for any amounts that are due by me/us in connection with my/our loan with Bridgit.';

const ACCOUNT_HOLDER_AUTHORITY_TEXT =
  'I/we acknowledge that I am / we are either an account holder(s) or an authorised signatory(ies) on the account ' +
  'specified above.';

export default {
  components: { ServiceAgreementLink },
  props: {
    value: { type: Object, required: true },
    errors: { type: Object, default: () => ({}) },
    // The post-settlement modal states the same thing in its own banner above the fields, so it
    // turns this off rather than saying it twice.
    showUsageNote: { type: Boolean, default: true },
    // Fetched at runtime by the parent dialog. Empty until it resolves, and stays empty when there
    // is nothing usable, in which case the agreement phrase renders as plain text.
    serviceAgreementUrl: { type: String, default: '' },
  },
  data() {
    return {
      BSB_MAX_LENGTH: BSB_LENGTH,
      ACCOUNT_NUMBER_MAX_LENGTH,
      ACCOUNT_NAME_MAX_LENGTH,
    };
  },
  computed: {
    // The slot renders the linked version; this plain-text copy stays the checkbox's accessible
    // value, so the two must say the same thing.
    directDebitAuthorityLabel() {
      return DIRECT_DEBIT_AUTHORITY_TEXT;
    },
    // No slot on this one — it carries no link, so the plain label is what renders.
    accountHolderAuthorityLabel() {
      return ACCOUNT_HOLDER_AUTHORITY_TEXT;
    },
  },
  methods: {
    sanitizeDigits,
    sanitizeText,
    updateField(fieldName, fieldValue) {
      this.$emit('update', { ...this.value, [fieldName]: fieldValue });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/global.scss';

.info-box {
  background: rgba($sky-blue-color, 0.12);
  color: $secondary-color;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
  line-height: 1.6;
}

.inputs {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
}

// Taller fields and a lighter placeholder, matching the field proportions in the design. Scoped
// here rather than on TextField, which every other form in the portal uses.
.inputs :deep(.p-inputtext) {
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;
}

.inputs :deep(.p-inputtext::placeholder) {
  color: rgba($secondary-color, 0.38);
  opacity: 1;
}

// PrimeFlex centres the box against the label, which floats it into the middle of a three-line
// acknowledgement. The design aligns it to the first line.
.authority :deep(.p-field-checkbox) {
  align-items: flex-start;
  gap: 0.75rem;
}

.authority :deep(.p-checkbox) {
  margin-top: 0.125rem;
}

// Shared with the next of kin step: the acknowledgements sit a step below body copy in the design.
.authority :deep(.label-text) {
  font-size: 0.875rem;
  line-height: 1.4;
}

// PrimeFlex gives every checkbox field a bottom margin, which spaces the two acknowledgements. On
// the last one it becomes trailing space that pushes the dialog footer below where the design has it.
.authority:last-child :deep(.p-field-checkbox) {
  margin-bottom: 0;
}

.bank-details-step .authority {
  padding-bottom: 0.5rem;
}

.error-text {
  color: red;
  font-size: 14px;
}
</style>
